// @ts-strict-ignore
import { createSelector } from 'reselect';
import type { State } from '~/store';
import {
  selectHasNodeContainers,
  selectNodeLineageForAnchorNode,
  selectWorkflowGraph,
} from './graph';
import type { ContainerHighlight, FolderNode, WorkflowNode } from 'flow/service/graph';
import {
  computeNodeState,
  filterNodesBySearch,
  getNodeIds,
  getNodeLabel,
  groupNodesById,
  isFolderNode,
  isSnippetNode,
} from 'flow/service/graph';
import { selectNodeFolderState, selectSearchMatchIndex } from './ui';
import { selectMergeDiff } from './snippets';
import { ActionType } from 'flow/store/engine/constants';
import { isActionOfType } from 'flow/service/actions';
import {
  isChildOfSnippetAction,
  isExternalSnippetChild,
  isInternalSnippetChild,
  isNodePartOfSelectedNodeSnippet,
  isSnippetChildAction,
} from 'flow/service/actions/snippets';

export const selectWorkflowNodeUIState = createSelector(
  [
    (state: State) => state.workflowEditor.ui.hoveredNodeId,
    (state: State) => state.workflowEditor.ui.focusedNodeId,
    (state: State) => state.workflowEditor.ui.activeNodeEditorTab,
    (state: State) => state.workflowEditor.ui.highlightedNodeId,
    (state: State) => state.workflowEditor.ui.floatedAnchorNodeId,
    (state: State) => state.workflowEditor.ui.nodeContainers,
  ],
  (
    hoveredNodeId,
    focusedNodeId,
    activeNodeEditorTab,
    highlightedNodeId,
    floatedAnchorNodeId,
    nodeContainers,
  ) => ({
    focusedNodeId,
    activeNodeEditorTab,
    floatedAnchorNodeId,
    hoveredNodeId,
    highlightedNodeId,
    nodeContainers,
  }),
);

export const selectFolderNode = createSelector(
  [
    selectWorkflowGraph,
    selectHasNodeContainers,
  ],
  (graph, { hasNodeFolder }): FolderNode => {
    if (!hasNodeFolder) {
      return null;
    }
    return graph.nodes.find(isFolderNode);
  },
);

export const selectFolderNodeChildren = createSelector(
  [
    selectFolderNode,
  ],
  folderNode => (folderNode ? folderNode.data.children : null),
);

export const selectCollapsedInputHasMatches = createSelector(
  [
    selectFolderNode,
    (state: State) => state.workflowEditor.ui.search.query,
  ],
  (inputAttributeFolder, searchQuery) => {
    if (!inputAttributeFolder) return false;
    return (
      // nested nodes are only populated in data.children when collapsed
      filterNodesBySearch(inputAttributeFolder.data.children, searchQuery).length > 0
    );
  },
);

export const selectCollapsedSnippetIdsWithMatches = createSelector(
  [
    selectWorkflowGraph,
    selectHasNodeContainers,
    (state: State) => state.workflowEditor.ui.nodeContainers.snippets,
    (state: State) => state.workflowEditor.ui.search.query,
  ],
  (graph, { hasSnippets }, snippetContainerState, searchQuery) => {
    if (!hasSnippets || !searchQuery) return [];

    const matchedSnippetIds: WorkflowNode['id'][] = [];

    graph.nodes.forEach(node => {
      if (!isSnippetNode(node)) return;

      const { isCollapsed } = snippetContainerState[node.id];
      if (!isCollapsed) return;
      if (filterNodesBySearch(node.data.children, searchQuery).length === 0) return;

      matchedSnippetIds.push(node.id);
    });

    return matchedSnippetIds;
  },
);

export const selectNodesMatchingSearch = createSelector(
  [
    selectWorkflowGraph,
    (state: State) => state.workflowEditor.ui.search.query,
  ],
  (graph, searchQuery) => filterNodesBySearch(graph.nodes, searchQuery),
);

export const selectNodeIdsMatchingSearch = createSelector(
  [
    selectNodesMatchingSearch,
  ],
  nodesMatchingSearch => getNodeIds(nodesMatchingSearch),
);

export const selectNodeAtSearchMatchIndex = createSelector(
  [
    selectSearchMatchIndex,
    selectNodesMatchingSearch,
  ],
  (searchMatchIndex, matchedNodes) => matchedNodes.at(searchMatchIndex),
);

export const selectNodesById = createSelector(
  [
    selectWorkflowGraph,
  ],
  graph => groupNodesById(graph.nodes),
);


export const selectNodeState = createSelector(
  [
    (state: State) => state.workflowEditor.ui.hoveredNodeId,
    (state: State) => state.workflowEditor.ui.focusedNodeId,
    (state: State) => state.workflowEditor.ui.activeNodeEditorTab,
    (state: State) => state.workflowEditor.ui.highlightedNodeId,
    (state: State) => state.workflowEditor.ui.search,
    selectNodeLineageForAnchorNode,
    selectNodeIdsMatchingSearch,
    selectNodesById,
    selectMergeDiff,
    selectNodeFolderState,
    (state: State, nodeId: string) => nodeId,
  ],
  (
    hoveredNodeId,
    focusedNodeId,
    activeNodeEditorTab,
    highlightedNodeId,
    searchState,
    nodeLineage,
    nodeIdsMatchingSearch,
    nodesById,
    mergeDiff,
    nodeFolder,
    nodeId,
  ) => computeNodeState({
    nodePartial: {
      nodeId,
      nodeLabel: getNodeLabel(nodesById[nodeId]),
    },
    uiStatePartial: {
      activeNodeEditorTab,
      focusedNodeId,
      highlightedNodeId,
      hoveredNodeId,
      searchState,
      nodeFolder,
    },
    nodeLineage,
    nodeIdsMatchingSearch,
    mergeDiff,
  }),
);

/**
 * Returns snippet highlight for snippet children (purple border and/or shadow).
 * When the focused node is the snippet action or an internal snippet child (within the container),
 *      the external nodes should have border (included).
 * When the focused node is any snippet child (external or internal), the snippet action node -> included.
 * When the focused node is any snippet node (external or internal), node -> focused.
 */
export const selectContainerHighlight = createSelector(
  [
    (state: State) => state.workflowEditor.ui.focusedNodeId,
    (state: State) => state.workflowEditor.workflowData?.actions,
    (_state: State, nodeId: string) => nodeId,
  ],
  (focusedNodeId, actions, nodeId): ContainerHighlight => {
    if (!focusedNodeId || !actions) return null;
    const focusedAction = actions[focusedNodeId];
    const action = actions[nodeId];
    if (!focusedAction || !action) return null;

    const isFocused = nodeId === focusedNodeId;
    const isSnippetParent = isActionOfType(action, ActionType.SNIPPET);
    const isFocusedChildOfThisSnippet = isChildOfSnippetAction({
      action: focusedAction,
      snippetParentActionPublicId: action.publicId,
    });

    if (isSnippetParent && !isFocused && isFocusedChildOfThisSnippet) {
      return 'included';
    }

    if (isFocused && (isSnippetParent || isSnippetChildAction(action))) return 'focused';
    if (!isExternalSnippetChild(action)) return null;

    const isSnippetParentFocused = isActionOfType(focusedAction, ActionType.SNIPPET);
    const focusedInSameSnippet = isSnippetParentFocused
      ? isChildOfSnippetAction({ action, snippetParentActionPublicId: focusedAction.publicId })
      : isInternalSnippetChild(focusedAction)
        && isNodePartOfSelectedNodeSnippet({ action, focusedAction });

    return focusedInSameSnippet ? 'included' : null;
  },
);
